import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const quotes = [
    { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
    { text: "Talent wins games, but teamwork wins championships.", author: "Michael Jordan" },
    { text: "Hire character. Train skill.", author: "Peter Schutz" },
    { text: "The best way to predict the future is to create it.", author: "Peter Drucker" },
    { text: "Great vision without great people is irrelevant.", author: "Jim Collins" },
    { text: "People are not your most important asset. The right people are.", author: "Jim Collins" },
    { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
    { text: "You miss 100% of the shots you don't take.", author: "Wayne Gretzky" },
    { text: "It's not about having the right opportunities. It's about handling them right.", author: "Mark Hunter" },
    { text: "Every accomplishment starts with the decision to try.", author: "John F. Kennedy" },
    { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { text: "Opportunities don't happen. You create them.", author: "Chris Grosser" },
    { text: "Don't be afraid to give up the good to go for the great.", author: "John D. Rockefeller" },
    { text: "What you do today can improve all your tomorrows.", author: "Ralph Marston" },
    { text: "A company is only as good as the people it keeps.", author: "Mary Kay Ash" },
    { text: "Culture eats strategy for breakfast.", author: "Peter Drucker" },
    { text: "The strength of the team is each member. The strength of each member is the team.", author: "Phil Jackson" },
    { text: "Your work is going to fill a large part of your life. Don't settle.", author: "Steve Jobs" },
    { text: "In the middle of difficulty lies opportunity.", author: "Albert Einstein" },
    { text: "The future depends on what you do today.", author: "Mahatma Gandhi" },
    { text: "Coming together is a beginning, staying together is progress, and working together is success.", author: "Henry Ford" },
    { text: "Nothing great was ever achieved without enthusiasm.", author: "Ralph Waldo Emerson" },
    { text: "Start where you are. Use what you have. Do what you can.", author: "Arthur Ashe" },
    { text: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
    { text: "Excellence is not a skill. It's an attitude.", author: "Ralph Marston" },
    { text: "The harder you work for something, the greater you'll feel when you achieve it.", author: "Anonymous" },
    { text: "Dream big. Start small. Act now.", author: "Robin Sharma" },
    { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
    { text: "It always seems impossible until it's done.", author: "Nelson Mandela" },
    { text: "Your limitation — it's only your imagination.", author: "Anonymous" },
    { text: "Push yourself, because no one else is going to do it for you.", author: "Anonymous" },
  ];

  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const todayQuote = quotes[dayOfYear % quotes.length];

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_hiring-metrics-2/artifacts/p4vfljno_LOGO%20.png" 
              alt="HRassistance Logo" 
              className="h-14 w-14 object-contain"
            />
            <div>
              <h1 className="text-4xl font-extrabold tracking-tight text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>HRassistance</h1>
              <p className="text-slate-500 text-sm">Performance tracking for recruitment excellence</p>
            </div>
          </div>

          <div className="bg-white border border-blue-100 rounded shadow-sm p-6">
            <h2 className="text-xl font-bold text-blue-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Sign in to your account</h2>
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="login-email" className="text-xs font-bold uppercase tracking-wider text-blue-900">Email</Label>
                <Input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="you@hrassistance.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  className="mt-2 rounded border-blue-200 focus-visible:ring-blue-600"
                />
              </div>
              <div>
                <Label htmlFor="login-password" className="text-xs font-bold uppercase tracking-wider text-blue-900">Password</Label>
                <Input
                  id="login-password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="Enter your password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  className="mt-2 rounded border-blue-200 focus-visible:ring-blue-600"
                />
              </div>
              <Button 
                type="submit" 
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold py-3 active:scale-95 transition-transform"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <p className="text-xs text-slate-400 mt-4 text-center">Contact your Admin if you need an account</p>
          </div>
        </div>
      </div>

      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}
      >
        <div className="absolute inset-0 bg-blue-900 bg-opacity-70 flex items-center justify-center p-12">
          <div className="text-center max-w-lg">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-300 mb-6">Quote of the Day</p>
            <h2 className="text-3xl font-extrabold text-white mb-6 leading-snug" data-testid="daily-quote" style={{ fontFamily: 'Manrope, sans-serif' }}>
              "{todayQuote.text}"
            </h2>
            <p className="text-lg text-blue-200 italic" data-testid="quote-author">— {todayQuote.author}</p>
            <div className="mt-10 pt-6 border-t border-blue-700/50">
              <p className="text-sm text-blue-300 font-semibold">Track. Measure. Improve.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
